import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { shouldDisplayPortalResultMessage } from '../api';

const SCORE_30_ROW_LABEL = '30% from the institution';

const normalizeScoreLabel = (score) => String(score ?? '').trim();

const isNumericScore = (score) => {
  const value = normalizeScoreLabel(score);
  return value !== '' && !Number.isNaN(Number(value));
};

const isNaScore = (score) => normalizeScoreLabel(score).toUpperCase() === 'NA';

const isDisqualifiedScore = (score) => {
  const value = normalizeScoreLabel(score).toLowerCase();
  return value === 'disqualified' || value === 'ng';
};

const getTotalDisplay = (result) => {
  if (result?.total === undefined || result?.total === null || result?.total === '') {
    const calculatedTotal = (result?.subjects || []).reduce((sum, subj) => {
      if (isNumericScore(subj.score)) {
        return sum + Number(subj.score);
      }
      return sum;
    }, 0);

    return { type: 'numeric', value: calculatedTotal };
  }

  const totalLabel = normalizeScoreLabel(result.total);
  if (totalLabel.toUpperCase() === 'NA') {
    return { type: 'na', value: 'NA' };
  }
  if (totalLabel.toLowerCase() === 'disqualified') {
    return { type: 'disqualified', value: 'Disqualified' };
  }

  const numericTotal = Number(result.total);
  if (!Number.isNaN(numericTotal)) {
    return { type: 'numeric', value: numericTotal };
  }

  return { type: 'text', value: totalLabel };
};

export default function RemedialResultPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [complaintData, setComplaintData] = useState({
    category: '',
    description: '',
    phoneNumber: ''
  });
  const [complaintReference, setComplaintReference] = useState('');
  const [copyConfirmed, setCopyConfirmed] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [isSubmittingComplaint, setIsSubmittingComplaint] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const complaintCategories = [
    { value: 'result_not_found', label: 'My result not found' },
    { value: 'disqualified_no_reason', label: 'My result disqualified without reason' },
    { value: 'wrong_gender', label: 'Wrong gender information' },
    { value: 'other', label: 'Other' }
  ];

  const result = location.state?.resultData;
  const resultMessage = shouldDisplayPortalResultMessage(location.state?.resultMessage || '')
    ? (location.state?.resultMessage || '')
    : '';
  const shouldShowResultMessage = Boolean(resultMessage);
  const is2016ShortYear = String(result?.short_year || '').trim() === '2016';
  const subjectScoreSuffix = is2016ShortYear ? '/70%' : '/100';
  const score30Suffix = '/30';
  const totalScoreSuffix = is2016ShortYear ? '/100' : '';
  const subjectScaleDescription = is2016ShortYear ? '70%' : '100';
  const showScore30Row = is2016ShortYear && result?.score_30 !== undefined && result?.score_30 !== null;

  // Robust icon mapping
  const subjectIcons = {
    English: 'fa-language',
    Maths: 'fa-calculator',
    Math: 'fa-calculator',
    Biology: 'fa-leaf',
    Chemistry: 'fa-flask',
    Physics: 'fa-atom',
    History: 'fa-landmark',
    Geography: 'fa-globe',
  };

  // Use backend total when available, with safe fallbacks for NA/Disqualified
  const totalDisplay = result ? getTotalDisplay(result) : { type: 'text', value: 'N/A' };
  const total = totalDisplay.type === 'numeric' ? totalDisplay.value : null;
  // 2016 total is out of 100 (30% institution + 70% subjects); other cohorts use subject-sum pass marks.
  let passMark = is2016ShortYear ? 50 : 250;
  if (!is2016ShortYear && result && result.stream_name && result.stream_name.toLowerCase().includes('social')) {
    passMark = 200;
  }
  if (!is2016ShortYear && result && result.is_blind && result.is_blind.toLowerCase() === 'yes') {
    passMark = 150;
  }
  const showPassFailSummary = totalDisplay.type === 'numeric';
  const passed = showPassFailSummary && total !== null ? total >= passMark : false;
  const isComplaintAllowed = Number(result?.complaint_status ?? 1) === 1;

  const handleComplaintSubmit = async () => {
    if (!isComplaintAllowed) {
      setValidationError('Complaint period is over.');
      return;
    }

    if (!complaintData.category || !complaintData.description.trim() || !complaintData.phoneNumber.trim()) {
      setValidationError('Please fill in all required fields.');
      return;
    }

    if (complaintData.phoneNumber.trim().length > 10) {
      setValidationError('Phone number must not exceed 10 characters.');
      return;
    }

    setValidationError('');
    setIsSubmittingComplaint(true);

    try {
      const API_KEY = process.env.REACT_APP_API_KEY || 'EAP-2024-7X9Y-2Z3W-4V5U-6T7S-8R9Q-0P1O';
      const API_URL = process.env.REACT_APP_API_URL || 'https://eap.ethernet.edu.et/api';

      const response = await fetch(`${API_URL}/remedial-complaints/submit`, {
        method: 'POST',
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          username: result.username,
          full_name: result.full_name || 'N/A',
          category: complaintData.category,
          description: complaintData.description,
          phoneNumber: complaintData.phoneNumber,
          short_year: result.short_year || '',
          stream_name: result.stream_name || '',
          institution_name: result.institution_name || ''
        })
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setShowComplaintModal(false);
        setShowSuccessModal(true);
        setComplaintData({ category: '', description: '', phoneNumber: '' });
        setValidationError('');
        setComplaintReference(data.data?.reference_number || 'N/A');
      } else {
        setValidationError(data.message || 'Failed to submit complaint. Please try again.');
      }
    } catch (error) {
      console.error('Complaint submission error:', error);
      setValidationError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmittingComplaint(false);
    }
  };

  const handleCancelComplaint = () => {
    setShowComplaintModal(false);
    setComplaintData({ category: '', description: '', phoneNumber: '' });
    setValidationError('');
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
  };

  const copyReferenceNumber = async () => {
    try {
      await navigator.clipboard.writeText(complaintReference);
      setCopyConfirmed(true);
      setTimeout(() => setCopyConfirmed(false), 2000);
    } catch (err) {
      console.error('Failed to copy reference number:', err);
      alert('Failed to copy reference number. Please copy it manually.');
    }
  };

  const formatSubjectScoreForPdf = (score, suffix = subjectScoreSuffix) => {
    if (isDisqualifiedScore(score)) return 'Disqualified';
    if (isNaScore(score)) return 'NA';
    if (!score || normalizeScoreLabel(score) === 'No Result') return 'No Result';
    if (isNumericScore(score)) return `${score}${suffix}`;
    return normalizeScoreLabel(score);
  };

  const formatTotalForPdf = () => {
    if (totalDisplay.type === 'na') return 'NA';
    if (totalDisplay.type === 'disqualified') return 'Disqualified';
    if (totalDisplay.type === 'numeric') {
      const value = Number.isInteger(totalDisplay.value)
        ? String(totalDisplay.value)
        : totalDisplay.value.toFixed(2);
      return `${value}${totalScoreSuffix}`;
    }
    return String(totalDisplay.value || 'N/A');
  };

  const formatStatusForPdf = () => {
    if (totalDisplay.type === 'na') return 'NA';
    if (totalDisplay.type === 'disqualified') return 'DISQUALIFIED';
    if (totalDisplay.type === 'numeric') return passed ? 'PASSED' : 'FAILED';
    return 'N/A';
  };

  const generatePDF = async () => {
    if (!result) {
      console.error('No result data available');
      return;
    }

    setIsDownloading(true);

    try {
      const verificationUrl = `${window.location.origin}/verify-remedial/${encodeURIComponent(result.username)}`;
      const qrCodeDataUrl = await QRCode.toDataURL(verificationUrl, {
        width: 80,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });

      const loadImageToBase64 = async (imagePath, fallbackSvg) => {
        try {
          let imageResponse;
          try {
            imageResponse = await fetch(imagePath);
          } catch (fetchError) {
            imageResponse = await fetch(`.${imagePath}`);
          }

          if (!imageResponse.ok) {
            throw new Error(`HTTP error! status: ${imageResponse.status}`);
          }

          const imageBlob = await imageResponse.blob();
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new Image();

          return await new Promise((resolve, reject) => {
            img.onload = () => {
              canvas.width = img.width;
              canvas.height = img.height;
              ctx.drawImage(img, 0, 0);
              resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = () => reject(new Error('Image failed to load'));
            img.src = URL.createObjectURL(imageBlob);
          });
        } catch (error) {
          console.warn(`Could not load image from ${imagePath}, using fallback:`, error);
          return fallbackSvg;
        }
      };

      const logoFallback = 'data:image/svg+xml;base64,' + btoa(`
        <svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#1e40af;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#3b82f6;stop-opacity:1" />
            </linearGradient>
          </defs>
          <rect width="80" height="80" rx="16" fill="url(#logoGradient)"/>
          <text x="40" y="50" font-family="Arial, sans-serif" font-size="32" font-weight="bold" text-anchor="middle" fill="white">M</text>
        </svg>
      `);

      const eaesFallback = 'data:image/svg+xml;base64,' + btoa(`
        <svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="eaesGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#059669;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#10b981;stop-opacity:1" />
            </linearGradient>
          </defs>
          <rect width="80" height="80" rx="16" fill="url(#eaesGradient)"/>
          <text x="40" y="50" font-family="Arial, sans-serif" font-size="24" font-weight="bold" text-anchor="middle" fill="white">EAES</text>
        </svg>
      `);

      const logoDataUrl = await loadImageToBase64('/logo.png', logoFallback);
      const eaesLogoDataUrl = await loadImageToBase64('/eaes.png', eaesFallback);

      const subjects = Array.isArray(result.subjects) ? result.subjects : [];
      const score30PdfRow = showScore30Row
        ? `
          <tr>
            <td style="padding: 5px 10px; color: #111827; font-weight: 500; background: white; font-size: 10px; border-right: 1px solid #d1d5db; border-bottom: 1px solid #9ca3af;">${SCORE_30_ROW_LABEL}</td>
            <td style="padding: 5px 10px; text-align: center; background: white; font-size: 10px; border-bottom: 1px solid #9ca3af;">
              <span style="font-size: 11px; font-weight: bold; color: #111827;">${formatSubjectScoreForPdf(result.score_30, score30Suffix)}</span>
            </td>
          </tr>
        `
        : '';
      const subjectRows = subjects.map((subj) => {
        const scoreLabel = formatSubjectScoreForPdf(subj.score);
        const scoreColor = isDisqualifiedScore(subj.score)
          ? 'color: #b91c1c;'
          : isNaScore(subj.score)
            ? 'color: #6b7280;'
            : 'color: #111827;';

        return `
          <tr>
            <td style="padding: 5px 10px; color: #111827; font-weight: 500; background: white; font-size: 10px; border-right: 1px solid #d1d5db; border-bottom: 1px solid #9ca3af;">${subj.name || 'N/A'}</td>
            <td style="padding: 5px 10px; text-align: center; background: white; font-size: 10px; border-bottom: 1px solid #9ca3af;">
              <span style="font-size: 11px; font-weight: bold; ${scoreColor}">${scoreLabel}</span>
            </td>
          </tr>
        `;
      }).join('');

      const totalLabel = formatTotalForPdf();
      const statusLabel = formatStatusForPdf();
      const statusColor = statusLabel === 'PASSED'
        ? '#059669'
        : (statusLabel === 'FAILED' || statusLabel === 'DISQUALIFIED')
          ? '#dc2626'
          : '#6b7280';

      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'fixed';
      tempDiv.style.left = '-10000px';
      tempDiv.style.top = '0';
      tempDiv.innerHTML = `
    <div id="remedial-certificate" style="width: 794px; min-height: 1123px; background: #ffffff; position: relative; overflow: visible; font-family: 'Inter', Arial, sans-serif; display: flex; flex-direction: column; box-sizing: border-box;">
        
        <div style="position: absolute; top: 0; left: 0; width: 180px; height: 180px; overflow: hidden;">
            <div style="position: absolute; left: -68px; top: 68px; width: 270px; transform: rotate(-45deg); transform-origin: top left; box-shadow: 0 6px 15px rgba(0,0,0,0.3);">
                <div style="height: 12px; background: linear-gradient(135deg, #782c07, #f97316); margin-bottom: 10px; border-radius: 6px;"></div>
                <div style="height: 24px; background: linear-gradient(135deg, #6e1010, #ef4444); margin-bottom: 10px; border-radius: 9px;"></div>
                <div style="height: 45px; background: linear-gradient(135deg, #1d3368, #3b82f6); border-radius: 12px;"></div>
            </div>
        </div>
        
        <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); font-size: 100px; color: rgba(0, 0, 0, 0.08); font-weight: bold; z-index: 50; pointer-events: none; letter-spacing: 8px;">TEMPORARY</div>
        
        <section style="text-align: center; margin-bottom: 12px; padding-top: 36px;">
            <div style="display: flex; justify-content: center; align-items: center; margin-bottom: 10px; gap: 28px;">
                <img src="${logoDataUrl}" alt="Ministry Logo" style="width: 80px; height: 80px; border-radius: 14px; box-shadow: 0 8px 25px rgba(30, 64, 175, 0.3);">
                <div style="display: flex; flex-direction: column; justify-content: center; text-align: center;">
                    <h1 style="font-size: 18px; font-weight: 700; color: #1f2937; margin: 0; letter-spacing: -0.5px;">የኤ.ፊ.ድ.ሪ ትምህርት ሚኒስቴር</h1>
                    <h1 style="font-size: 16px; font-weight: 600; color: #374151; margin: 4px 0; letter-spacing: -0.3px;">FDRE Ministry of Education</h1>
                </div>
                <img src="${eaesLogoDataUrl}" alt="EAES Logo" style="width: 96px; height: 96px; border-radius: 14px; box-shadow: 0 8px 25px rgba(5, 150, 105, 0.3);">
            </div>
            <div style="height: 4px; background: linear-gradient(90deg, #1e40af, #3b82f6, #f97316, #ea580c); margin: 0 40px 12px 40px; border-radius: 2px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);"></div>
        </section>
        
        <main style="padding: 0 40px; position: relative; z-index: 10; flex: 1; display: flex; flex-direction: column;">
            <section style="text-align: center; margin-bottom: 12px;">
                <h2 style="font-size: 17px; font-weight: bold; color: #1f2937; margin-bottom: 4px;">Remedial Examination Certificate</h2>
            </section>

            <section style="margin-bottom: 12px;">
                <h3 style="font-size: 14px; font-weight: 600; color: #1f2937; margin-bottom: 10px; border-bottom: 2px solid #e5e7eb; padding-bottom: 6px;">Student Information</h3>
                
                <div style="display: flex; gap: 20px; margin-bottom: 8px;">
                    <div style="flex: 1; min-width: 0;">
                        <div style="display: flex; align-items: flex-start; margin-bottom: 6px;">
                            <span style="font-weight: 600; color: #374151; width: 80px; font-size: 12px; flex-shrink: 0;">Full Name:</span>
                            <div style="flex: 1; position: relative;">
                                <span style="color: #111827; font-size: 12px; word-wrap: break-word; line-height: 1.3; display: block; padding-bottom: 6px;">${result.full_name || 'N/A'}</span>
                                <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background-image: linear-gradient(to right, #9ca3af 50%, transparent 50%); background-size: 4px 1px; background-repeat: repeat-x;"></div>
                            </div>
                        </div>
                    </div>
                    <div style="flex: 1; min-width: 0;">
                        <div style="display: flex; align-items: flex-start; margin-bottom: 6px;">
                            <span style="font-weight: 600; color: #374151; width: 80px; font-size: 12px; flex-shrink: 0;">Username:</span>
                            <div style="flex: 1; position: relative;">
                                <span style="color: #111827; font-size: 12px; word-wrap: break-word; line-height: 1.3; display: block; padding-bottom: 6px;">${(result.username || 'N/A').toUpperCase()}</span>
                                <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background-image: linear-gradient(to right, #9ca3af 50%, transparent 50%); background-size: 4px 1px; background-repeat: repeat-x;"></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div style="display: flex; gap: 20px; margin-bottom: 8px;">
                    <div style="flex: 1; min-width: 0;">
                        <div style="display: flex; align-items: flex-start; margin-bottom: 6px;">
                            <span style="font-weight: 600; color: #374151; width: 80px; font-size: 12px; flex-shrink: 0;">Institution:</span>
                            <div style="flex: 1; position: relative;">
                                <span style="color: #111827; font-size: 12px; word-wrap: break-word; line-height: 1.3; display: block; padding-bottom: 6px;">${result.institution_name || 'N/A'}</span>
                                <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background-image: linear-gradient(to right, #9ca3af 50%, transparent 50%); background-size: 4px 1px; background-repeat: repeat-x;"></div>
                            </div>
                        </div>
                    </div>
                    <div style="flex: 1; min-width: 0;">
                        <div style="display: flex; align-items: flex-start; margin-bottom: 6px;">
                            <span style="font-weight: 600; color: #374151; width: 80px; font-size: 12px; flex-shrink: 0;">Gender:</span>
                            <div style="flex: 1; position: relative;">
                                <span style="color: #111827; font-size: 12px; word-wrap: break-word; line-height: 1.3; display: block; padding-bottom: 6px;">${result.gender || 'N/A'}</span>
                                <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background-image: linear-gradient(to right, #9ca3af 50%, transparent 50%); background-size: 4px 1px; background-repeat: repeat-x;"></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div style="display: flex; gap: 20px; margin-bottom: 8px;">
                    <div style="flex: 1; min-width: 0;">
                        <div style="display: flex; align-items: flex-start; margin-bottom: 6px;">
                            <span style="font-weight: 600; color: #374151; width: 80px; font-size: 12px; flex-shrink: 0;">Stream:</span>
                            <div style="flex: 1; position: relative;">
                                <span style="color: #111827; font-size: 12px; word-wrap: break-word; line-height: 1.3; display: block; padding-bottom: 6px;">${result.stream_name || 'N/A'}</span>
                                <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background-image: linear-gradient(to right, #9ca3af 50%, transparent 50%); background-size: 4px 1px; background-repeat: repeat-x;"></div>
                            </div>
                        </div>
                    </div>
                    <div style="flex: 1; min-width: 0;">
                        <div style="display: flex; align-items: flex-start; margin-bottom: 6px;">
                            <span style="font-weight: 600; color: #374151; width: 80px; font-size: 12px; flex-shrink: 0;">Exam Year:</span>
                            <div style="flex: 1; position: relative;">
                                <span style="color: #111827; font-size: 12px; word-wrap: break-word; line-height: 1.3; display: block; padding-bottom: 6px;">${result.short_year || 'N/A'}</span>
                                <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background-image: linear-gradient(to right, #9ca3af 50%, transparent 50%); background-size: 4px 1px; background-repeat: repeat-x;"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section style="margin-bottom: 10px;">
                <h3 style="font-size: 12px; font-weight: 600; color: #1f2937; margin-bottom: 6px; border-bottom: 1px solid #e5e7eb; padding-bottom: 3px;">Subject Results</h3>
                <div style="background: white; border: 1px solid #9ca3af; border-radius: 6px; overflow: hidden; position: relative;">
                    <table style="width: 100%; position: relative; z-index: 10; background: white; border-collapse: collapse; border: 1px solid #9ca3af;">
                        <thead style="background: linear-gradient(135deg, #60a5fa, #3b82f6); color: white;">
                            <tr>
                                <th style="padding: 5px 10px; text-align: left; font-weight: 600; font-size: 9px; text-transform: uppercase; letter-spacing: 0.3px; border-bottom: 2px solid #1e40af; border-right: 1px solid rgba(255,255,255,0.3);">Subject</th>
                                <th style="padding: 5px 10px; text-align: center; font-weight: 600; font-size: 9px; text-transform: uppercase; letter-spacing: 0.3px; border-bottom: 2px solid #1e40af;">Score</th>
                            </tr>
                        </thead>
                        <tbody style="background: white;">
                            ${subjectRows || `
                              <tr>
                                <td colspan="2" style="padding: 8px 10px; text-align: center; color: #6b7280; font-size: 10px;">No subject results available</td>
                              </tr>
                            `}
                            ${score30PdfRow}
                            <tr>
                              <td style="padding: 6px 10px; color: #111827; font-weight: 700; background: #f8fafc; font-size: 10px; border-right: 1px solid #d1d5db;">Total Score${showPassFailSummary ? ` (Pass mark: ${passMark})` : ''}</td>
                              <td style="padding: 6px 10px; text-align: center; background: #f8fafc; font-size: 10px;">
                                <span style="font-size: 12px; font-weight: bold; color: #1f2937;">${totalLabel}</span>
                              </td>
                            </tr>
                            <tr>
                              <td style="padding: 6px 10px; color: #111827; font-weight: 700; background: #f8fafc; font-size: 10px; border-right: 1px solid #d1d5db;">Status</td>
                              <td style="padding: 6px 10px; text-align: center; background: #f8fafc; font-size: 10px;">
                                <span style="font-size: 11px; font-weight: 700; color: ${statusColor};">${statusLabel}</span>
                              </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            <section style="margin-bottom: 10px;">
                <div style="background: linear-gradient(135deg, #eff6ff, #e0e7ff); border-left: 4px solid #2563eb; padding: 12px; border-radius: 0 12px 12px 0; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
                    <p style="font-size: 12px; color: #1f2937; font-weight: 500; margin: 0; line-height: 1.4;">
                        This is to certify that the above-mentioned student has completed the Remedial Examination 
                        as recorded by the Ministry of Education, Federal Democratic Republic of Ethiopia.
                    </p>
                </div>
            </section>

            <section style="margin-bottom: 8px; margin-top: 16px;">
                <div style="display: flex; align-items: center; justify-content: flex-end; gap: 16px; padding-right: 20px;">
                    <div style="text-align: center;">
                        <div style="width: 70px; height: 70px; background: #f9fafb; border: 2px solid #e5e7eb; border-radius: 12px; display: flex; align-items: center; justify-content: center; margin: 0 auto 6px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
                            <img src="${qrCodeDataUrl}" alt="Verification QR Code" style="width: 60px; height: 60px;">
                        </div>
                        <p style="font-size: 11px; font-weight: 600; color: #374151; margin-bottom: 2px;">Scan to Verify</p>
                        <p style="font-size: 9px; color: #6b7280;">Certificate Verification</p>
                    </div>
                    <div style="text-align: left;">
                        <div style="display: flex; align-items: center; margin-bottom: 4px;">
                            <span style="font-size: 11px; font-weight: 600; color: #374151;">Certificate Verification</span>
                        </div>
                        <p style="font-size: 9px; color: #6b7280; line-height: 1.4; max-width: 180px;">
                            Scan this QR code to verify the authenticity of this certificate 
                            through the official Ministry of Education portal.
                        </p>
                    </div>
                </div>
            </section>
        </main>

        <footer style="background: linear-gradient(135deg, #f9fafb, #f3f4f6); padding: 12px 40px 14px; border-top: 1px solid #e5e7eb; position: relative; z-index: 20; border-radius: 0 0 12px 12px; margin-top: 8px;">
            <div style="text-align: center;">
                <div style="display: flex; align-items: center; justify-content: center; margin-bottom: 8px;">
                    <svg style="width: 14px; height: 14px; margin-right: 8px; fill: #3b82f6;" viewBox="0 0 24 24">
                        <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                    </svg>
                    <span style="font-size: 11px; color: #6b7280;">Generated on: ${new Date().toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</span>
                </div>
                <div style="border-top: 1px solid #d1d5db; padding-top: 8px;">
                    <p style="font-size: 10px; color: #6b7280; font-weight: 500; margin-bottom: 4px;">OFFICIAL DISCLAIMER</p>
                    <p style="font-size: 10px; color: #6b7280; line-height: 1.4; background: rgba(255, 255, 255, 0.7); padding: 6px 12px; border-radius: 8px; border: 1px solid #e5e7eb; margin: 0;">
                        This certificate is generated electronically by the Ministry of Education Remedial Examination Portal. 
                        Any alterations or modifications to this document will render it invalid. For verification purposes, 
                        please visit https://result.ethernet.edu.et
                    </p>
                </div>
                <div style="height: 3px; background: linear-gradient(90deg, #1e40af, #3b82f6, #f97316, #ea580c); margin: 10px 0 6px 0; border-radius: 2px;"></div>
                <div style="padding-top: 4px;">
                    <p style="font-size: 9px; color: #9ca3af; margin: 0;">
                        Federal Democratic Republic of Ethiopia, Ministry of Education | P.O. Box 1367, Addis Ababa
                    </p>
                </div>
            </div>
        </footer>
    </div>
      `;

      document.body.appendChild(tempDiv);

      try {
        const certificateEl = tempDiv.querySelector('#remedial-certificate');
        const canvas = await html2canvas(certificateEl, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          scrollX: 0,
          scrollY: 0,
          windowWidth: certificateEl.scrollWidth,
          windowHeight: certificateEl.scrollHeight
        });

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = 210;
        const pageHeight = 297;
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (imgHeight <= pageHeight) {
          pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        } else {
          // Scale down so full certificate (including disclaimer) fits on one page
          const scale = pageHeight / imgHeight;
          const scaledWidth = imgWidth * scale;
          const scaledHeight = pageHeight;
          const xOffset = (pageWidth - scaledWidth) / 2;
          pdf.addImage(imgData, 'PNG', xOffset, 0, scaledWidth, scaledHeight);
        }

        const fileName = `remedial_exam_result_${result.username}_${new Date().toISOString().split('T')[0]}.pdf`;
        pdf.save(fileName);
      } finally {
        document.body.removeChild(tempDiv);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const renderSubjectScore = (score, suffix = subjectScoreSuffix) => {
    if (isDisqualifiedScore(score)) {
      return (
        <span className="inline-block bg-red-100 text-red-700 border border-red-200 px-3 py-1.5 rounded-full font-bold text-sm">
          Disqualified
        </span>
      );
    }

    if (isNaScore(score)) {
      return (
        <span className="inline-block bg-gray-100 text-gray-600 border border-gray-200 px-3 py-1.5 rounded-full font-bold text-sm">
          NA
        </span>
      );
    }

    if (!score || normalizeScoreLabel(score) === 'No Result') {
      return (
        <span className="inline-block bg-gray-100 text-gray-600 border border-gray-200 px-3 py-1.5 rounded-full font-bold text-sm">
          No Result
        </span>
      );
    }

    if (isNumericScore(score)) {
      return (
        <>
          <span className="text-lg font-semibold text-gray-800">{score}</span>
          <span className="text-gray-500">{suffix}</span>
        </>
      );
    }

    return (
      <span className="inline-block bg-gray-100 text-gray-700 border border-gray-200 px-3 py-1.5 rounded-full font-bold text-sm">
        {normalizeScoreLabel(score)}
      </span>
    );
  };

  const renderTotalScore = () => {
    if (totalDisplay.type === 'na') {
      return (
        <span className="inline-block bg-gray-100 text-gray-700 border border-gray-200 px-4 py-2 rounded-full font-bold text-lg">
          NA
        </span>
      );
    }

    if (totalDisplay.type === 'disqualified') {
      return (
        <span className="inline-block bg-red-100 text-red-700 border border-red-200 px-4 py-2 rounded-full font-bold text-lg">
          Disqualified
        </span>
      );
    }

    if (totalDisplay.type === 'numeric') {
      const value = Number.isInteger(totalDisplay.value) ? totalDisplay.value : totalDisplay.value.toFixed(2);
      return (
        <>
          <span className="text-2xl font-bold text-purple-900">{value}</span>
          {totalScoreSuffix ? <span className="text-lg text-gray-500 ml-1">{totalScoreSuffix}</span> : null}
        </>
      );
    }

    return (
      <span className="text-2xl font-bold text-purple-900">{totalDisplay.value}</span>
    );
  };

  // Early return after all hooks and effects
  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow text-center">
          <h2 className="text-xl font-bold mb-4 text-red-600">No result data found.</h2>
          <button onClick={() => navigate('/remedial-usernamepage')} className="px-6 py-2 bg-purple-700 text-white rounded-lg mt-4">Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="container mx-auto px-4 py-8">
        {/* Back Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/remedial-usernamepage')}
            className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            <i className="fa-solid fa-arrow-left mr-2" style={{ color: '#6c47b6' }}></i>
            <span>Back</span>
          </button>
        </div>
        {/* Results Header with Download Button */}
        <section className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <h2 className="text-xl font-bold flex items-center mb-4 sm:mb-0" style={{ color: '#6c47b6' }}>
              <i className="fa-solid fa-book-open mr-3 text-lg" style={{ color: '#6c47b6' }} />
              Remedial Exam Result
            </h2>
            <button
              onClick={generatePDF}
              disabled={isDownloading || !result}
              className="inline-flex items-center px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#6c47b6' }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#5936a3';
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#6c47b6';
              }}
              title={isDownloading ? 'Generating PDF...' : 'Download PDF Certificate'}
            >
              {isDownloading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                  Generating...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-download mr-2"></i>
                  Download PDF
                </>
              )}
            </button>
          </div>
        </section>
        {/* Student Info Card */}
        <section className="bg-white rounded-2xl shadow-lg mb-8 p-6 sm:p-8 border border-gray-100" style={{ fontFamily: 'Roboto, \"Helvetica Neue\", Arial, sans-serif' }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
            {[
              { icon: 'fa-user', label: 'Username', value: result.username },
              { icon: 'fa-id-card', label: 'Full Name', value: result.full_name },
              { icon: 'fa-calendar', label: 'Exam Year', value: result.short_year },
              { icon: 'fa-venus-mars', label: 'Gender', value: result.gender },
              { icon: 'fa-building-columns', label: 'Institution', value: result.institution_name },
              { icon: 'fa-book', label: 'Stream', value: result.stream_name },
            ].map(({ icon, label, value }) => (
              <div key={label} className="p-4 rounded-xl bg-gradient-to-br from-gray-50 to-purple-50">
                <div className="flex items-center mb-2">
                  <i className={`fa-solid ${icon} mr-2`} style={{ color: '#6c47b6' }} />
                  <p className="text-sm text-gray-500">{label}</p>
                </div>
                <p className="font-normal text-gray-900 break-words">{value}</p>
              </div>
            ))}
          </div>
        </section>
        {shouldShowResultMessage && (
          <section className="mb-6">
            <div className={`rounded-xl border p-4 ${
              resultMessage.toLowerCase().includes('not eligible')
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-blue-50 border-blue-200 text-blue-800'
            }`}>
              <div className="flex items-start gap-3">
                <i className={`fa-solid ${
                  resultMessage.toLowerCase().includes('not eligible')
                    ? 'fa-ban'
                    : 'fa-circle-info'
                } mt-0.5`} />
                <p className="text-sm font-medium">{resultMessage}</p>
              </div>
            </div>
          </section>
        )}
        {/* Subject Results Table (Thin Row Separators) */}
        <section className="bg-white border border-purple-100 rounded-2xl shadow-sm p-8 mb-4">
          <h2 className="text-2xl font-bold text-purple-800 mb-4 text-center">Subject Results</h2>
          <div className="max-w-2xl mx-auto mb-8">
            <div className="flex items-center gap-3 bg-purple-50 border-l-4 border-purple-400 rounded-lg p-4 shadow-sm">
              <i className="fa-solid fa-info-circle text-purple-600 text-xl flex-shrink-0"></i>
              <span className="text-gray-800 text-base">
                {is2016ShortYear ? (
                  <>
                    Remedial subject scores below are out of <span className="font-semibold">{subjectScaleDescription}</span>.
                    Where applicable, <span className="font-semibold">30% from the institution</span> is listed at the end of the table.
                    Total score is out of <span className="font-semibold">100</span>.
                  </>
                ) : (
                  <>
                    Each subject is graded out of <span className="font-semibold">{subjectScaleDescription}</span>. To pass, you must achieve a total score above 50%. Your total score is the sum of all subject scores.
                  </>
                )}
              </span>
            </div>
          </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-purple-100 to-purple-200 border-b border-purple-300">
                    <th className="text-left py-6 px-6 font-semibold text-gray-800 text-lg">
                      <div className="flex items-center">
                        <i className="fas fa-book-open text-purple-600 mr-2"></i>
                        Subject
                      </div>
                    </th>
                    <th className="text-center py-6 px-6 font-semibold text-gray-800 text-lg">
                      <div className="flex items-center justify-center">
                        <i className="fas fa-trophy text-purple-600 mr-2"></i>
                        Score
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {result.subjects.map((subj, idx) => (
                    <tr key={subj.name} className="border-b border-purple-200">
                      <td className="py-4 px-6">
                        <div className="flex items-center">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${
                            subj.name === 'Biology' ? 'bg-green-100' :
                            subj.name === 'English' ? 'bg-blue-100' :
                            subj.name === 'Maths' || subj.name === 'Mathematics' ? 'bg-purple-100' :
                            subj.name === 'Physics' ? 'bg-indigo-100' :
                            subj.name === 'Chemistry' ? 'bg-red-100' :
                            subj.name === 'History' ? 'bg-yellow-100' :
                            subj.name === 'Geography' ? 'bg-teal-100' : 'bg-gray-100'
                          }`}>
                            <i className={`fas ${
                              subj.name === 'Biology' ? 'fa-leaf text-green-600' :
                              subj.name === 'English' ? 'fa-language text-blue-600' :
                              subj.name === 'Maths' || subj.name === 'Mathematics' ? 'fa-calculator text-purple-600' :
                              subj.name === 'Physics' ? 'fa-atom text-indigo-600' :
                              subj.name === 'Chemistry' ? 'fa-flask text-red-600' :
                              subj.name === 'History' ? 'fa-landmark text-yellow-600' :
                              subj.name === 'Geography' ? 'fa-globe text-teal-600' : 'fa-book text-gray-600'
                            }`}></i>
                          </div>
                          <span className="font-medium text-gray-800">{subj.name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        {renderSubjectScore(subj.score)}
                      </td>
                    </tr>
                  ))}
                  {showScore30Row && (
                    <tr className="border-b border-purple-200">
                      <td className="py-4 px-6">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center mr-3 bg-amber-100">
                            <i className="fas fa-building-columns text-amber-600" />
                          </div>
                          <span className="font-medium text-gray-800">{SCORE_30_ROW_LABEL}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        {renderSubjectScore(result.score_30, score30Suffix)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
          {/* Total and Status (Polished Card) */}
          <section className="bg-white border border-purple-100 rounded-2xl shadow-sm p-8 mb-8">
            <div className="flex flex-col sm:flex-row justify-between items-center">
              <div className="flex items-center mb-4 sm:mb-0">
                <span className="font-semibold mr-2 text-lg text-gray-800">Total Score:</span>
                {renderTotalScore()}
                {showPassFailSummary && (
                  <span className="ml-3 text-sm text-gray-500">(Pass mark: {passMark})</span>
                )}
              </div>
              {showPassFailSummary && (
                <div className="flex items-center space-x-4">
                  {result.is_blind === 'Yes' && (
                    <div className="inline-flex items-center bg-blue-100 text-blue-700 border border-blue-200 px-3 py-1 rounded-full font-medium text-sm shadow-sm">
                      <i className="fa-solid fa-eye-slash mr-1"></i>
                      Visual Impairment
                    </div>
                  )}
                  <div className="flex items-center">
                    <span className="font-semibold mr-2 text-lg text-gray-800">Status:</span>
                    <span className={passed ? 'inline-flex items-center bg-green-100 text-green-700 border border-green-200 px-4 py-2 rounded-full font-bold text-lg shadow-sm' : 'inline-flex items-center bg-red-100 text-red-700 border border-red-200 px-4 py-2 rounded-full font-bold text-lg shadow-sm'}>
                      {passed ? (
                        <><i className="fa-solid fa-check-circle mr-2" />Passed</>
                      ) : (
                        <><i className="fa-solid fa-times-circle mr-2" />Failed</>
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
            {/* Mobile Download Button removed */}
          </section>
          {/* Note Section */}
          <section className="mt-12 p-6 sm:p-8 bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl border border-purple-100 shadow-md">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start">
                <i className="fa-solid fa-circle-info text-2xl flex-shrink-0 mt-1" style={{ color: '#6c47b6' }} />
                <div className="ml-4">
                  <h4 className="text-lg font-semibold mb-2" style={{ color: '#6c47b6' }}>Important Note</h4>
                  <p className="text-sm mb-2" style={{ color: '#6c47b6' }}>
                    This is your official remedial examination result. For any discrepancies or concerns, please submit a complaint.
                    {!isComplaintAllowed ? ' Complaint period is over.' : ''}
                    {' '}For any updates,
                    <span
                      className="inline-flex items-center cursor-pointer transition-colors duration-200 text-sm font-medium ml-1 hover:opacity-80 underline"
                      style={{ color: '#6c47b6' }}
                      onClick={() => window.open('https://t.me/moeremedialexamsupport', '_blank')}
                    >
                      <i className="fa-brands fa-telegram mr-1"></i>
                      join our official Telegram channel
                    </span>
                    .
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <button
                  onClick={() => isComplaintAllowed && setShowComplaintModal(true)}
                  className={`flex items-center justify-center px-6 py-3 rounded-lg shadow-md transition-colors ${
                    isComplaintAllowed ? 'text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  style={isComplaintAllowed ? { backgroundColor: '#6c47b6' } : undefined}
                  onMouseEnter={(e) => {
                    if (isComplaintAllowed) e.target.style.backgroundColor = '#5936a3';
                  }}
                  onMouseLeave={(e) => {
                    if (isComplaintAllowed) e.target.style.backgroundColor = '#6c47b6';
                  }}
                  disabled={!isComplaintAllowed}
                >
                  <i className="fa-solid fa-flag mr-2" />
                  {isComplaintAllowed ? 'Submit Complaint' : 'Complaint period is over'}
                </button>
              </div>
            </div>
          </section>
        </main>

      {/* Complaint Modal */}
      {showComplaintModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <i className="fa-solid fa-flag mr-3" style={{ color: '#6c47b6' }}></i>
                  Submit Complaint
                </h2>
                <button
                  onClick={handleCancelComplaint}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fa-solid fa-times text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              <form onSubmit={(e) => { e.preventDefault(); handleComplaintSubmit(); }}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Complaint Category *
                  </label>
                  <select
                    value={complaintData.category}
                    onChange={(e) => setComplaintData({ ...complaintData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-purple-500"
                    style={{ '--tw-ring-color': '#6c47b6' }}
                    required
                  >
                    <option value="">Select a category</option>
                    {complaintCategories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description * ({complaintData.description.length}/255)
                  </label>
                  <textarea
                    value={complaintData.description}
                    onChange={(e) => setComplaintData({ ...complaintData, description: e.target.value })}
                    rows={4}
                    maxLength={255}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-purple-500 ${
                      complaintData.description.length > 250 ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Please describe your complaint in detail..."
                    required
                  />
                  {complaintData.description.length > 250 && (
                    <p className="text-sm text-red-600 mt-1">
                      Warning: You're approaching the character limit ({complaintData.description.length}/255)
                    </p>
                  )}
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number * (Format: 09*********)
                  </label>
                  <input
                    type="tel"
                    value={complaintData.phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 10) {
                        setComplaintData({ ...complaintData, phoneNumber: value });
                      }
                    }}
                    maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-purple-500"
                    placeholder="09*********"
                    required
                  />
                  {complaintData.phoneNumber.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {complaintData.phoneNumber.length}/10 characters
                    </p>
                  )}
                </div>

                {validationError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{validationError}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCancelComplaint}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingComplaint || !isComplaintAllowed}
                    className="flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#6c47b6' }}
                  >
                    {isSubmittingComplaint ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                        Submitting...
                      </>
                    ) : (
                      'Submit Complaint'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <i className="fa-solid fa-check-circle mr-3 text-green-500"></i>
                  Success
                </h2>
                <button
                  onClick={handleCloseSuccessModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fa-solid fa-times text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fa-solid fa-check text-2xl text-green-500"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Complaint Submitted
                </h3>
                <p className="text-gray-600 mb-6">
                  Your complaint has been submitted successfully. We have received your request and will review it shortly.
                </p>
                <div className="p-4 mb-4 rounded-lg border text-center" style={{
                  backgroundColor: 'rgba(108, 71, 182, 0.05)',
                  borderColor: 'rgba(108, 71, 182, 0.2)'
                }}>
                  <p className="text-sm font-semibold mb-3" style={{ color: '#6c47b6' }}>
                    Save This Reference Number:
                  </p>
                  <div className="flex items-center justify-center mb-3">
                    <span className="font-mono font-bold text-base mr-3" style={{ color: '#6c47b6' }}>{complaintReference}</span>
                    <button
                      onClick={copyReferenceNumber}
                      className="px-3 py-1 rounded text-sm font-medium hover:bg-purple-100 transition-colors"
                      style={{ color: '#6c47b6' }}
                      title="Copy reference number"
                    >
                      <i className="fa-solid fa-copy mr-1"></i>
                      Copy
                    </button>
                    {copyConfirmed && (
                      <span className="ml-2 text-sm font-medium text-green-600 flex items-center">
                        <i className="fa-solid fa-check mr-1"></i>
                        Copied!
                      </span>
                    )}
                  </div>
                  <p className="text-xs opacity-80" style={{ color: '#6c47b6' }}>
                    You can check the complaint result when announced using this reference number.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <div className="flex justify-center">
                <button
                  onClick={handleCloseSuccessModal}
                  className="px-8 py-3 text-white rounded-lg transition-colors duration-200 font-medium"
                  style={{ backgroundColor: '#6c47b6' }}
                >
                  <i className="fa-solid fa-check mr-2"></i>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    );
  } 